import React from 'react';
import styled from '@emotion/styled';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useLanguage } from '../../../Hooks/useLanguage';
import type { RouteChallenge } from '../../../Model/Engagement/RouteChallenge/RouteChallenge';
import { Theme } from '../../../Theme/Theme';

type ExtraMileCopy = {
  pointsBeyondGoal: string;
  pointsLabel: string;
  unavailable: string;
};

const copy: Record<string, ExtraMileCopy> = {
  en: {
    pointsBeyondGoal: 'points beyond the goal',
    pointsLabel: '{{points}} points beyond the goal',
    unavailable: 'Extra points unavailable',
  },
  de: {
    pointsBeyondGoal: 'Punkte über dem Ziel',
    pointsLabel: '{{points}} Punkte über dem Ziel',
    unavailable: 'Zusatzpunkte nicht verfügbar',
  },
  fr: {
    pointsBeyondGoal: 'points au-delà de l’objectif',
    pointsLabel: '{{points}} points au-delà de l’objectif',
    unavailable: 'Points supplémentaires indisponibles',
  },
  it: {
    pointsBeyondGoal: 'punti oltre l’obiettivo',
    pointsLabel: '{{points}} punti oltre l’obiettivo',
    unavailable: 'Punti extra non disponibili',
  },
};

const ExtraMileSection = styled.section<{ $compact: boolean }>`
  margin-top: ${({ $compact }) => ($compact ? 16 : 24)}px;
  padding-top: ${({ $compact }) => ($compact ? 16 : 24)}px;
  border-top: 1px solid currentColor;
  border-color: rgba(255, 255, 255, 0.3);
`;

const Counter = styled.div<{ $compact: boolean }>`
  font-family: Montserrat, sans-serif;
  font-size: ${({ $compact }) => ($compact ? 32 : 44)}px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.03em;
  line-height: 1.2;
  overflow-wrap: anywhere;
`;

const PointsLabel = styled.div`
  font-size: 14px;
  margin-top: 4px;
`;

const RankingExtraPoints = styled.div`
  color: ${Theme.Colors.textColor};
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
  margin-top: 4px;
  max-width: 120px;
  overflow-wrap: anywhere;
`;

type ChallengeExtraMileProps = {
  challenge: Pick<RouteChallenge, 'started'>;
  totalPoints: number;
  extraPoints: number | null | undefined;
  goalPoints: number;
  compact?: boolean;
  variant?: 'section' | 'ranking';
};

export const ChallengeExtraMile = ({
  challenge,
  totalPoints,
  extraPoints,
  goalPoints,
  compact = false,
  variant = 'section',
}: ChallengeExtraMileProps) => {
  const { t } = useTrans();
  const { language } = useLanguage();
  const languageCode = language.toLowerCase().split('-')[0];
  const text = copy[languageCode] ?? copy.en;

  if (!challenge.started || goalPoints <= 0 || totalPoints < goalPoints) {
    return null;
  }

  const unavailable = extraPoints == null;
  const points = unavailable ? '—' : `+${Math.round(extraPoints).toLocaleString(language)}`;
  const label = unavailable
    ? t('challenge.extraPoints.unavailable', { defaultValue: text.unavailable })
    : t('challenge.extraPoints.pointsLabel', { defaultValue: text.pointsLabel, points });

  if (variant === 'ranking') {
    return (
      <RankingExtraPoints data-testid="challenge-ranking-extra-points">
        {label}
      </RankingExtraPoints>
    );
  }

  return (
    <ExtraMileSection
      $compact={compact}
      data-testid="challenge-extra-mile"
      aria-label={label}
    >
      <Counter $compact={compact} data-testid="challenge-extra-mile-points" aria-hidden="true">
        {points}
      </Counter>
      <PointsLabel aria-hidden="true">
        {unavailable ? label : t('challenge.extraPoints.pointsBeyondGoal', { defaultValue: text.pointsBeyondGoal })}
      </PointsLabel>
    </ExtraMileSection>
  );
};
